﻿import { eColumnType, IBooleanFieldRecording, IClientLogger, IColumnDefinition, IDateFieldRecording, IField, IGetTableRuleArgs, ILogMessage, INumberFieldRecording, IObjectFieldRecording, IQuoteProduct, IRuleArgs, ISetQuoteProductFieldValueArgs, IStringFieldRecording, IUploadFieldRecording, IUploadValue } from "@models";
import { KPromise } from "@rules/kpromise";
import { eDateUnit } from "@tools";

/*
    The js in this file is included with every rule that is run (on client and server and clara plugin).
    Only put stuff in here that every rule relies on!
*/


let _tableDb: { [id: string]: any[] } = {};
export function getTables(e: IRuleArgs, args: IGetTableRuleArgs): Q.IPromise<any[][]> {
    _tableDb = _tableDb || {};
    if (args.ids.every(id => _tableDb[id] != null)) {
        return new KPromise(args.ids.map(id => _tableDb[id]));
    } else {
        return e.getTables(args).then(tables => {
            tables.forEach((t, i) => {
                var dateCols: IColumnDefinition[] = t.columns.filter(c => (c.type || c["Type"]) == eColumnType.date);
                if (dateCols.length) {
                    t.data.forEach(row => {
                        dateCols.forEach(dateCol => {
                            var dateColName = dateCol.name || dateCol["Name"];
                            row[dateColName] = new Date(row[dateColName]);
                        });
                    });
                }
                _tableDb[args.ids[i]] = t.data;
            });
            return tables.map(t => t.data);
        }) as any;
    }
}

export function getUploadFieldUrl(e: IRuleArgs, field: IField) {
    let value: IUploadValue = field.value;
    return e.baseUrl + "/api/quotes/filefromconfiguratorfield/" + value.path;
}

export function clearTableCache() {
    _tableDb = {};
}

export function r(v) {
    if (v === undefined) v = null;
    return v;
}

let _globalVariables = {};
export function declareGlobalVariable(variableName: string, defaultValue: any) {
    if (typeof (_globalVariables[variableName]) === "undefined") {
        _globalVariables[variableName] = defaultValue;
    }
}

export function setGlobalVariable(variableName: string, value: any) {
    _globalVariables[variableName] = value;
}

export function getGlobalVariable(variableName: string): any {
    return _globalVariables[variableName];
}

export function indexOf(haystack: string, needle: string): number {
    if (!haystack) return -1;
    return haystack.indexOf(needle);
}

export function moveItemInArray(array: any[], item: any, index: number) {
    let idx = array.indexOf(item);
    if (idx < 0) return;
    array.splice(idx, 1);
    array.splice(index, 0, item);
}

export function stringLength(str: string): number {
    if (!str) return 0;
    return str.length;
}

export function strReplace(search: string, replaceWith: string, subject: string, useRegex: boolean = true): string {
    if (!search) search = "";
    if (!replaceWith) replaceWith = "";
    if (!subject) subject = "";
    if (useRegex) {
        return subject.replace(new RegExp(search, "gi"), replaceWith);
    }
    else {
        var esc = search.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
        var rgx = new RegExp(esc, 'ig');
        return subject.replace(rgx, replaceWith);
    }
}

export function translateTemplateLiteral(tmpltStr: string, obj: any): string {
    // If there are no properties in the object don't bother interpolating  
    if (!obj) return tmpltStr;
    const propsArr = Object.getOwnPropertyNames(obj);
    let interpolatedStr = tmpltStr;
    propsArr.forEach(propName => {
        interpolatedStr = interpolatedStr.replace(
            new RegExp("\\${" + propName + "}", "gi"),
            obj[propName]
        );
    });
    return interpolatedStr;
}


/*
    Strmatches is a simplified version of JS's str.match function. Instead of returning an
    array of matches, it simply returns true/false depending on if a match is found.
*/
export function strMatch(search: string, subject: string): boolean {
    if (!search) search = "";
    if (!subject) subject = "";
    var match = subject.match(search);
    return match != null && match.length > 0;
}

export function getCharCodeAt(subject: string, index: number): number {
    if (!subject) subject = "";
    return subject.charCodeAt(index);
}

export function toUpperCase(str: string): string {
    if (!str) return "";
    return str.toUpperCase();
}

export function toLowerCase(str: string): string {
    if (!str) return "";
    return str.toLowerCase();
}

export function capitalize(str: string): string {
    if (!str) return "";
    return str.capitalize();
}

export function toTitleCase(str: string): string {
    if (!str) return "";
    return str.toPascalCase();
}

export function trim(str: string): string {
    if (!str) return "";
    return str.trim();
}

export function objToString(v: any): string {
    if (typeof v == "undefined" || v === null) return "";
    return v.toString();
}

export function stringSubstring(str: string, start: number, end?: number): string {
    if (!str) return "";
    return str.substring(start, end);
}

export function isNumeric(obj) {
    // Need to check for empty strings and trim the object because isNaN() "incorrectly" returns false for
    // empty strings, whitespace, and arrays.
    return !isNaN(parseFloat(obj)) && isFinite(obj);
}

// export function sleep(time: number): Q.IPromise<any> {
//     let d = Q.defer();
//     setTimeout(() => {
//         d.resolve(null);
//     }, time);
//     return d.promise;
// }

export function getQuoteProductFieldValue(quoteProduct: IQuoteProduct, fieldName: string) {
    let cp = quoteProduct.configuredProduct;
    if (!cp) return null;
    let field: any = cp.fields.find(f => f.name == fieldName);
    if (!field) return null;
    if (field.stringValue) return field.stringValue;
    if (field.numberValue) return field.numberValue;
    if (field.objectValue) return field.objectValue;
    if (field.dateValue) return field.dateValue;
    return field.booleanValue;
}


export function setQuoteProductFieldValue(args: ISetQuoteProductFieldValueArgs) {
    let cp = args.quoteProduct.configuredProduct;
    if (cp) {
        let field = cp.fields.find(f => f.name == args.fieldName);
        if (field) {
            if (field.hasOwnProperty("stringValue")) {
                (field as IStringFieldRecording).stringValue = args.value;
            } else if (field.hasOwnProperty("numberValue")) {
                (field as INumberFieldRecording).numberValue = args.value;
            } else if (field.hasOwnProperty("booleanValue")) {
                (field as IBooleanFieldRecording).booleanValue = args.value;
            } else if (field.hasOwnProperty("dateValue")) {
                (field as IDateFieldRecording).dateValue = args.value;
            } else if (field.hasOwnProperty("uploadValue")) {
                (field as IUploadFieldRecording).uploadValue = args.value;
            } else if (field.hasOwnProperty("objectValue")) {
                (field as IObjectFieldRecording).objectValue = args.value;
            }
        }
    }
}

export function factorial(value: number): number {
    let result = 1;

    for (let i = 2; i <= value; i++) {
        result = result * i;
    }

    return result;
}

export function padStringLeft(str: any, padChars: string) {
    return padChars.substring(str.toString().length) + str;
}

// Returns a new date with the added amount
export function dateAdd(date: Date, units: eDateUnit, amount: number): Date {
    let newDate = new Date(date.getTime());
    if (units == eDateUnit.date) {
        newDate.setDate(newDate.getDate() + amount);
    } else if (units == eDateUnit.fullYear) {
        newDate.setFullYear(newDate.getFullYear() + amount);
    } else if (units == eDateUnit.hours) {
        newDate.setHours(newDate.getHours() + amount);
    } else if (units == eDateUnit.minutes) {
        newDate.setMinutes(newDate.getMinutes() + amount);
    } else if (units == eDateUnit.month) {
        newDate.setMonth(newDate.getMonth() + amount);
    } else if (units == eDateUnit.seconds) {
        newDate.setSeconds(newDate.getSeconds() + amount);
    }
    return newDate;
}

export function formatDate(d: any, format: string): string {
    // Very simple implementation

    let date: Date;
    if (typeof d == "object") {
        date = d;
    } else if (typeof d == "string") {
        date = new Date(Date.parse(d));
    } else if (typeof d == "number") {
        date = new Date(d);
    } else {
        throw new Error("Date is in invalid format: " + (typeof d));
    }

    if (format == null) format = "short";

    let fullMonths = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"];
    
    let fullDays = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
    ];

    let keys = {
        "fullDate": (date: Date) => "$EEEE, $MMMM $d, $yyyy",
        "longDate": (date: Date) => "$MMMM $d, $yyyy",
        "mediumDate": (date: Date) => "$MMM $d, $yyyy",
        "shortDate": (date: Date) => "$M/$d/$yy",
        "mediumTime": (date: Date) => "$h:$mm:$ss $a",
        "shortTime": (date: Date) => "$h:$mm $a",
        "medium": (date: Date) => "$MMM $d, $yy $h:$mm:$ss $a",
        "short": (date: Date) => "$M/$d/$yy $h:$mm $a",

        "\\$yyyy": (date: Date) => date.getFullYear(),
        "\\$yy": (date: Date) => date.getFullYear() - 2000,
        "\\$dd": (date: Date) => padStringLeft(date.getDate(), "00"),
        "\\$d": (date: Date) => date.getDate(),
        "\\$HH": (date: Date) => padStringLeft(date.getHours(), "00"),
        "\\$H": (date: Date) => date.getHours(),
        "\\$hh": (date: Date) => padStringLeft(date.getHours() ? date.getHours() % 12 : 12, "00"),
        "\\$h": (date: Date) => date.getHours() ? date.getHours() % 12 : 12,
        "\\$mm": (date: Date) => padStringLeft(date.getMinutes(), "00"),
        "\\$m": (date: Date) => date.getMinutes(),
        "\\$ss": (date: Date) => padStringLeft(date.getSeconds(), "00"),
        "\\$s": (date: Date) => date.getSeconds(),
        "\\$Z": (date: Date) => date.getTimezoneOffset(),
        "\\$a": (date: Date) => date.getHours() > 12 ? "PM" : "AM",
        "\\$MMMM": (date: Date) => fullMonths[date.getMonth() % 12],
        "\\$MMM": (date: Date) => fullMonths[date.getMonth() % 12].substr(0, 3),
        "\\$MM": (date: Date) => padStringLeft(date.getMonth() + 1, "00"),
        "\\$M": (date: Date) => date.getMonth() + 1,
        "\\$EEEE": (date: Date) => fullDays[date.getDay()],
        "\\$EEE": (date: Date) => fullDays[date.getDay()].substr(0, 3)
    };

    for (let key in keys) {
        format = format.replace(new RegExp(key, "g"), keys[key](date));
    }
    return format;
}

// http://stackoverflow.com/questions/736513/how-do-i-parse-a-url-into-hostname-and-path-in-javascript
export function getLocation(href) {
    let match = href.match(/^(https?\:)\/\/(([^:\/?#]*)(?:\:([0-9]+))?)(\/[^?#]*)(\?[^#]*|)(#.*|)$/);
    if (match) {
        return {
            protocol: match[1],
            host: match[2],
            hostname: match[3],
            port: match[4],
            pathname: match[5],
            search: match[6],
            hash: match[7]
        };
    }
    return null;
}

export function getNestedConfigurator(config, e) {
    if (typeof config == "string") {
        return e.kom.getByName(config);
    }

    return config;
}

export function logout() {
    fetch("/api/auth/logout", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doNotReplay: true })
    })
        .then(() => {
            window.location.href = '/';
        })
        .catch(err => {
            console.error('Logout failed:', err);
        });
}

// http://stackoverflow.com/questions/1714786/querystring-encoding-of-a-javascript-object
export function toQueryString(obj: any, prefix?: string): string {
    let str = [];
    for (let p in obj) {
        if (obj.hasOwnProperty(p)) {
            let k = prefix ? prefix + "[" + p + "]" : p;
            let v = obj[p];
            str.push(typeof v == "object" ?
                toQueryString(v, k) :
                encodeURIComponent(k) + "=" + encodeURIComponent(v));
        }
    }
    return str.join("&");
}

export function roundNumber(num: number, interval: number, mode: string): number {
    let roundedNum;
    if (mode == "Math.ceil") roundedNum = Math.ceil(num / interval);
    else if (mode == "Math.floor") roundedNum = Math.floor(num / interval);
    else roundedNum = Math.round(num / interval);
    return parseFloat((interval * roundedNum).toFixed(getPrecision(interval)));
}

export function getPrecision(num: number): number {
    if(Math.floor(num) === num) return 0;
    return num.toString().split(".")[1].length || 0;
}

export class ClientLogger implements IClientLogger {
    public push(msg: ILogMessage) {
        if (typeof window !== "undefined" && window.console) {
            if (String(msg.type) === 'error') {
                window.console.error(msg.message);
            } else if (String(msg.type) === 'warning') {
                window.console.warn(msg.message);
            } else if (String(msg.type) === 'info') {
                window.console.info(msg.message);
            } else {
                window.console.log(msg.message);
            }
        }
    }
}
export function btoa(input: string): string {

    if (typeof window !== "undefined" && window.btoa) {
        return window.btoa(input);
    }

    let str = String (input);
    let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

    for (
        var block, charCode, idx = 0, map = chars, output = '';
        str.charAt (idx | 0) || (map = '=', idx % 1);
        output += map.charAt (63 & block >> 8 - idx % 1 * 8)
    ) {
        charCode = str.charCodeAt (idx += 3 / 4);

        if (charCode > 0xFF) {
            throw "'btoa' failed: The string to be encoded contains characters outside of the Latin1 range.";
        }

        block = block << 8 | charCode;
    }

    return output;
};

export function equalsAny(o: any, list: Array<any>): boolean{
    for(let n of list){
        if (o == n) return true;
    }
    return false;
}

export function navigateToUrl(url: string, newWindow: boolean) {
    if (newWindow) {
        window.open(url, "_blank");
    }
    else {
        window.location.href = url;
    }
}

export function convertLikeToRegex(sqlLike: string, notLike: boolean) {
    // Escape special regex characters
    // const escapedLike = sqlLike.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapedLike = sqlLike.replace(/[.*+?${}()|\\]/g, '\\$&');
    
    // Replace SQL wildcards with regex equivalents
    const regexLike = escapedLike.replace(/%/g, '.*').replace(/_/g, '.');
    
    // Add regex anchors to match the entire string
    const finalRegex = `^${regexLike}$`;
    
    return new RegExp(finalRegex);
}

export function arraySum(array: any[], callback: (item: any) => number) {
    let sum = 0;
    // Using array.forEach here instead of array.reduce because reduce doesn't apply the function on the initial element of the array.
    array.forEach((element) => {
        sum = sum + callback(element);
    });
    return sum;
}

export function arrayAverage(array: any[], callback: (item: any) => number) {
    const sum = arraySum(array, callback);
    return (sum / array.length);
}

export function arrayMin(array: any[], callback: (item: any) => number) {
    let min: number | undefined = undefined;
    array.forEach((element) => {
        const result = callback(element);
        if (result < min || min === undefined) {
            min = result;
        }
    });
    return min;
}

export function arrayMax(array: any[], callback: (item: any) => number) {
    let max: number | undefined = undefined;
    array.forEach((element) => {
        const result = callback(element);
        if (result > max || max === undefined) {
            max = result;
        }
    });
    return max;
}

export function generateGuid(args: any): string {
    if (!args || !args.guidLength) {
        args = { useStandardFormat: true }
    }

    let length = args.useStandardFormat ? 32 : args.guidLength;
    if (length < 8) {
        length = 8;
    } else if (length > 100) {
        length = 100;
    }

    let guid = 'x';
    guid = guid.padEnd(length, 'x');
    guid = guid.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });

    if (args.useStandardFormat) {
        guid = guid.slice(0, 8) + '-' + guid.slice(8, 12) + '-' + guid.slice(12, 16) + '-' + guid.slice(16, 20) + '-' + guid.slice(20, guid.length);
    }

    return guid;
}
